import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Info, XCircle, X } from "lucide-react";

// ===========================================================
// 全局对话框 / 提示系统
// ===========================================================
// 用法：
//   import { confirmDialog, alertDialog, toast } from "@/components/ui/dialog";
//   if (await confirmDialog({ title: "确定删除？" })) { ... }
//   await alertDialog({ title: "保存失败", message: e.message, tone: "error" });
//   toast.success("已保存");

type Tone = "info" | "success" | "warning" | "error";

interface ConfirmOpts {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone;
  destructive?: boolean;
}

interface AlertOpts {
  title: string;
  message?: string;
  tone?: Tone;
  okText?: string;
}

interface ToastItem {
  id: number;
  tone: Tone;
  message: string;
}

interface CustomNode {
  id: number;
  render: (close: (result: any) => void) => React.ReactNode;
  resolve: (v: any) => void;
}

// 事件总线
const bus = new EventTarget();
let nextId = 1;

export function confirmDialog(opts: ConfirmOpts): Promise<boolean> {
  return new Promise((resolve) => {
    bus.dispatchEvent(new CustomEvent("confirm", { detail: { opts, resolve } }));
  });
}

export function alertDialog(opts: AlertOpts): Promise<void> {
  return new Promise((resolve) => {
    bus.dispatchEvent(new CustomEvent("alert", { detail: { opts, resolve } }));
  });
}

/** 显示自定义内容对话框，render 函数接收 close(result) 回调，可用于 prompt/多选/表单等 */
export function customDialog<T = any>(render: (close: (result: T | null) => void) => React.ReactNode): Promise<T | null> {
  return new Promise((resolve) => {
    bus.dispatchEvent(new CustomEvent("custom", { detail: { render, resolve } }));
  });
}

export const toast = {
  show: (message: string, tone: Tone = "info") => {
    bus.dispatchEvent(new CustomEvent("toast", { detail: { message, tone } }));
  },
  info:    (m: string) => toast.show(m, "info"),
  success: (m: string) => toast.show(m, "success"),
  warning: (m: string) => toast.show(m, "warning"),
  error:   (m: string) => toast.show(m, "error"),
};

const TONE_META: Record<Tone, { icon: any; cls: string; bar: string }> = {
  info:    { icon: Info,           cls: "text-sky-600",     bar: "bg-sky-500" },
  success: { icon: CheckCircle2,   cls: "text-emerald-600", bar: "bg-emerald-500" },
  warning: { icon: AlertTriangle,  cls: "text-amber-600",   bar: "bg-amber-500" },
  error:   { icon: XCircle,        cls: "text-red-600",     bar: "bg-red-500" },
};

// ===========================================================
// Host 组件 —— 在 App 根挂一份
// ===========================================================
export function DialogHost() {
  const [confirmState, setConfirmState] = useState<{ opts: ConfirmOpts; resolve: (v: boolean) => void } | null>(null);
  const [alertState, setAlertState] = useState<{ opts: AlertOpts; resolve: () => void } | null>(null);
  const [customNodes, setCustomNodes] = useState<CustomNode[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const onConfirm = (e: Event) => setConfirmState((e as CustomEvent).detail);
    const onAlert = (e: Event) => setAlertState((e as CustomEvent).detail);
    const onCustom = (e: Event) => {
      const { render, resolve } = (e as CustomEvent).detail;
      const id = nextId++;
      setCustomNodes((arr) => [...arr, { id, render, resolve }]);
    };
    const onToast = (e: Event) => {
      const { message, tone } = (e as CustomEvent).detail;
      const id = nextId++;
      setToasts((t) => [...t, { id, message, tone }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
    };
    bus.addEventListener("confirm", onConfirm);
    bus.addEventListener("alert", onAlert);
    bus.addEventListener("custom", onCustom);
    bus.addEventListener("toast", onToast);
    return () => {
      bus.removeEventListener("confirm", onConfirm);
      bus.removeEventListener("alert", onAlert);
      bus.removeEventListener("custom", onCustom);
      bus.removeEventListener("toast", onToast);
    };
  }, []);

  // ESC 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmState) { confirmState.resolve(false); setConfirmState(null); return; }
      if (alertState) { alertState.resolve(); setAlertState(null); return; }
      if (customNodes.length > 0) {
        const last = customNodes[customNodes.length - 1]!;
        last.resolve(null);
        setCustomNodes((arr) => arr.filter((x) => x.id !== last.id));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmState, alertState, customNodes]);

  return (
    <>
      {/* Confirm */}
      {confirmState && (() => {
        const tone = confirmState.opts.tone || (confirmState.opts.destructive ? "warning" : "info");
        const Meta = TONE_META[tone];
        const Icon = Meta.icon;
        return (
          <Backdrop onClose={() => { confirmState.resolve(false); setConfirmState(null); }}>
            <div className="rounded-xl bg-card shadow-2xl border w-full max-w-sm overflow-hidden">
              <div className="p-5 flex items-start gap-3">
                <div className={`mt-0.5 ${Meta.cls}`}><Icon className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="font-semibold">{confirmState.opts.title}</div>
                  {confirmState.opts.description && (
                    <div className="text-sm text-muted-foreground mt-1">{confirmState.opts.description}</div>
                  )}
                </div>
              </div>
              <div className="px-5 py-3 bg-muted/30 border-t flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { confirmState.resolve(false); setConfirmState(null); }}>
                  {confirmState.opts.cancelText || "取消"}
                </Button>
                <Button size="sm"
                  variant={confirmState.opts.destructive ? "destructive" : "default"}
                  onClick={() => { confirmState.resolve(true); setConfirmState(null); }}>
                  {confirmState.opts.confirmText || "确定"}
                </Button>
              </div>
            </div>
          </Backdrop>
        );
      })()}

      {/* Alert */}
      {alertState && (() => {
        const tone = alertState.opts.tone || "info";
        const Meta = TONE_META[tone];
        const Icon = Meta.icon;
        return (
          <Backdrop onClose={() => { alertState.resolve(); setAlertState(null); }}>
            <div className="rounded-xl bg-card shadow-2xl border w-full max-w-sm overflow-hidden">
              <div className="p-5 flex items-start gap-3">
                <div className={`mt-0.5 ${Meta.cls}`}><Icon className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="font-semibold">{alertState.opts.title}</div>
                  {alertState.opts.message && (
                    <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">{alertState.opts.message}</div>
                  )}
                </div>
              </div>
              <div className="px-5 py-3 bg-muted/30 border-t flex justify-end">
                <Button size="sm" onClick={() => { alertState.resolve(); setAlertState(null); }}>
                  {alertState.opts.okText || "知道了"}
                </Button>
              </div>
            </div>
          </Backdrop>
        );
      })()}

      {/* Custom dialogs */}
      {customNodes.map((n) => (
        <Backdrop key={n.id} onClose={() => {
          n.resolve(null);
          setCustomNodes((arr) => arr.filter((x) => x.id !== n.id));
        }}>
          {n.render((result) => {
            n.resolve(result);
            setCustomNodes((arr) => arr.filter((x) => x.id !== n.id));
          })}
        </Backdrop>
      ))}

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[200] space-y-2 pointer-events-none">
        {toasts.map((t) => {
          const Meta = TONE_META[t.tone];
          const Icon = Meta.icon;
          return (
            <div key={t.id}
              className="pointer-events-auto rounded-lg bg-card border shadow-lg pl-1 pr-3 py-2.5 flex items-center gap-2 text-sm min-w-[220px] max-w-md animate-in slide-in-from-right">
              <div className={`w-1 self-stretch rounded-full ${Meta.bar}`} />
              <Icon className={`h-4 w-4 shrink-0 ${Meta.cls}`} />
              <span className="flex-1">{t.message}</span>
              <button onClick={() => setToasts((arr) => arr.filter((x) => x.id !== t.id))}
                className="text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="animate-in zoom-in-95 w-full max-w-md flex justify-center">
        {children}
      </div>
    </div>
  );
}
